/**
 * Tutorial guiado — dados puros (fases, passos, seletores).
 * Sem DOM, sem import do app.js. Testável em Node.
 */

/** Seletores que nunca podem receber advance:'click' (compra / paywall). */
export const PURCHASE_SELECTORS = Object.freeze([
  '#emeraldStoreNavButton',
  '#gamePassNavButton',
]);

/** Valor mínimo do saco de drops (IdleCoin) para abrir a Fase 4. */
export const DROPS_VALUE_THRESHOLD = 100;

/**
 * Fases do tutorial. Só uma roda por vez.
 * `trigger` / `triggers` = eventos; `minLevel` / `minDropValue` = predicados extras.
 */
export const TUTORIAL_PHASES = Object.freeze([
  Object.freeze({ id: 'core', order: 1, trigger: 'onboarding:done' }),
  Object.freeze({ id: 'capture', order: 2, trigger: 'wild:faint' }),
  Object.freeze({ id: 'supplies', order: 3, triggers: Object.freeze(['drops:gain', 'partner:lowhp']) }),
  Object.freeze({ id: 'economy', order: 4, trigger: 'drops:gain', minDropValue: DROPS_VALUE_THRESHOLD }),
  Object.freeze({ id: 'progress', order: 5, trigger: 'trainer:level', minLevel: 5 }),
  Object.freeze({ id: 'advanced', order: 6, trigger: 'trainer:level', minLevel: 10 }),
  Object.freeze({ id: 'social', order: 7, trigger: 'trainer:level', minLevel: 15 }),
]);

const mobileSkip = () => typeof globalThis.innerWidth === 'number' && globalThis.innerWidth <= 720;

/** @typedef {'next'|'click'|'event'|'any'} AdvanceType */

/** Todos os passos, em ordem de fase. */
export const TUTORIAL_STEPS = Object.freeze([
  // —— Fase 1 · CORE ——
  Object.freeze({
    id: 'welcome', phase: 'core', target: null, placement: 'center',
    titleKey: 'tut.welcome.title', bodyKey: 'tut.welcome.body',
    advance: Object.freeze({ type: 'next' }),
  }),
  Object.freeze({
    id: 'city-walk', phase: 'core', target: '.city-welcome', placement: 'bottom',
    titleKey: 'tut.cityWalk.title', bodyKey: 'tut.cityWalk.body',
    advance: Object.freeze({ type: 'next' }), ensure: 'city',
  }),
  Object.freeze({
    id: 'team-hud', phase: 'core', target: '#globalTeamHud', placement: 'left',
    titleKey: 'tut.teamHud.title', bodyKey: 'tut.teamHud.body',
    advance: Object.freeze({ type: 'next' }),
  }),
  Object.freeze({
    id: 'partner-bars', phase: 'core', target: '.team-pokemon-row', placement: 'left',
    titleKey: 'tut.partnerBars.title', bodyKey: 'tut.partnerBars.body',
    advance: Object.freeze({ type: 'next' }),
  }),
  Object.freeze({
    id: 'currency', phase: 'core', target: '.currency-row', placement: 'bottom',
    titleKey: 'tut.currency.title', bodyKey: 'tut.currency.body',
    advance: Object.freeze({ type: 'next' }), skipIf: mobileSkip,
  }),
  Object.freeze({
    id: 'open-atlas', phase: 'core', target: '#headerMapButton', placement: 'bottom',
    titleKey: 'tut.openAtlas.title', bodyKey: 'tut.openAtlas.body',
    advance: Object.freeze({ type: 'click' }),
  }),
  Object.freeze({
    // Mantém o id antigo para que quem parou no passo confuso receba a correção
    // sem exigir outro reset global do tutorial.
    id: 'pick-route', phase: 'core', target: '[data-atlas-type]:not([disabled])', placement: 'right',
    titleKey: 'tut.pickRoute.title', bodyKey: 'tut.pickRoute.body',
    advance: Object.freeze({ type: 'click' }),
    targetMatch: 'first-enabled',
    requires: (state) => !state?.running,
  }),
  Object.freeze({
    id: 'pick-level-one', phase: 'core', target: '[data-atlas-band="0"]', placement: 'top',
    titleKey: 'tut.pickLevelOne.title', bodyKey: 'tut.pickLevelOne.body',
    advance: Object.freeze({ type: 'click' }),
    requires: (state) => !state?.running,
  }),
  Object.freeze({
    id: 'pick-species', phase: 'core', target: '[data-atlas-species]', placement: 'top',
    titleKey: 'tut.pickSpecies.title', bodyKey: 'tut.pickSpecies.body',
    advance: Object.freeze({ type: 'click' }),
    targetMatch: 'first-enabled',
    requires: (state) => !state?.running,
  }),
  Object.freeze({
    id: 'start-hunt', phase: 'core', target: '.atlas-start:not([disabled])', placement: 'top',
    titleKey: 'tut.startHunt.title', bodyKey: 'tut.startHunt.body',
    advance: Object.freeze({ type: 'event', name: 'hunt:start' }),
    requires: (state) => !state?.running,
  }),
  // Os três passos seguintes falam da hunt em andamento. Sem `state.running` eles
  // apontariam para a tela de batalha atrás do modal do Atlas — que foi o bug.
  Object.freeze({
    id: 'battle-auto', phase: 'core', target: '.battle-status', placement: 'top',
    titleKey: 'tut.battleAuto.title', bodyKey: 'tut.battleAuto.body',
    advance: Object.freeze({ type: 'next' }), ensure: 'adventure',
    requires: (state) => !!state?.running,
  }),
  Object.freeze({
    id: 'enemy-hud', phase: 'core', target: '.route-combat-hud', placement: 'left',
    titleKey: 'tut.enemyHud.title', bodyKey: 'tut.enemyHud.body',
    advance: Object.freeze({ type: 'next' }), ensure: 'adventure',
    requires: (state) => !!state?.running,
  }),
  Object.freeze({
    id: 'rest-button', phase: 'core', target: '#restButton', placement: 'top',
    titleKey: 'tut.restButton.title', bodyKey: 'tut.restButton.body',
    advance: Object.freeze({ type: 'next' }),
    requires: (state) => !!state?.running,
  }),

  // —— Fase 2 · CAPTURA ——
  Object.freeze({
    id: 'capture-panel', phase: 'capture', target: '#captureFeedback', placement: 'top',
    titleKey: 'tut.capturePanel.title', bodyKey: 'tut.capturePanel.body',
    advance: Object.freeze({ type: 'next' }),
  }),
  Object.freeze({
    id: 'capture-ball', phase: 'capture', target: '[data-throw-faint]:not([disabled])', placement: 'top',
    titleKey: 'tut.captureBall.title', bodyKey: 'tut.captureBall.body',
    // Avança na TENTATIVA, não na captura: fugir é resultado normal e não pode travar o passo.
    advance: Object.freeze({ type: 'event', name: 'capture:attempt' }),
    targetMatch: 'first-enabled',
  }),
  Object.freeze({
    id: 'nav-captures', phase: 'capture', target: '#capturePanelButton', placement: 'bottom',
    titleKey: 'tut.navCaptures.title', bodyKey: 'tut.navCaptures.body',
    advance: Object.freeze({ type: 'click' }),
  }),
  Object.freeze({
    id: 'nav-mypokemon', phase: 'capture', target: '#myPokemonNavButton', placement: 'bottom',
    titleKey: 'tut.navMyPokemon.title', bodyKey: 'tut.navMyPokemon.body',
    advance: Object.freeze({ type: 'click' }),
  }),
  Object.freeze({
    id: 'team-detail', phase: 'capture', target: '#teamManagementList [data-team-member]', placement: 'right',
    titleKey: 'tut.teamDetail.title', bodyKey: 'tut.teamDetail.body',
    advance: Object.freeze({ type: 'next' }),
    targetMatch: 'first-enabled',
  }),

  // —— Fase 3 · SUPRIMENTOS ——
  Object.freeze({
    id: 'nav-inventory', phase: 'supplies', target: '#inventoryNavButton', placement: 'bottom',
    titleKey: 'tut.navInventory.title', bodyKey: 'tut.navInventory.body',
    advance: Object.freeze({ type: 'click' }),
  }),
  Object.freeze({
    id: 'potions', phase: 'supplies', target: '#inventorySections .inventory-section:nth-of-type(2)', placement: 'left',
    titleKey: 'tut.potions.title', bodyKey: 'tut.potions.body',
    advance: Object.freeze({ type: 'next' }),
  }),
  Object.freeze({
    id: 'nav-automation', phase: 'supplies', target: '#automationPanelButton', placement: 'bottom',
    titleKey: 'tut.navAutomation.title', bodyKey: 'tut.navAutomation.body',
    advance: Object.freeze({ type: 'click' }),
  }),
  Object.freeze({
    id: 'auto-potion', phase: 'supplies', target: '#autoPotion', placement: 'left',
    titleKey: 'tut.autoPotion.title', bodyKey: 'tut.autoPotion.body',
    advance: Object.freeze({ type: 'click' }),
  }),
  Object.freeze({
    id: 'auto-capture-vip', phase: 'supplies', target: '#autoCapture', placement: 'left',
    titleKey: 'tut.autoCaptureVip.title', bodyKey: 'tut.autoCaptureVip.body',
    advance: Object.freeze({ type: 'next' }),
  }),
  Object.freeze({
    id: 'heal-nurse', phase: 'supplies', target: '.nurse-npc [data-city-action="heal"]', placement: 'top',
    titleKey: 'tut.healNurse.title', bodyKey: 'tut.healNurse.body',
    advance: Object.freeze({ type: 'click' }), ensure: 'city',
  }),

  // —— Fase 4 · ECONOMIA ——
  Object.freeze({
    id: 'shop-npc', phase: 'economy', target: '.collector-shop-npc [data-city-action="shop"]', placement: 'top',
    titleKey: 'tut.shopNpc.title', bodyKey: 'tut.shopNpc.body',
    advance: Object.freeze({ type: 'click' }), ensure: 'city',
  }),
  Object.freeze({
    id: 'shop-sell', phase: 'economy', target: '#shopTabs [data-tab="sell"]', placement: 'bottom',
    titleKey: 'tut.shopSell.title', bodyKey: 'tut.shopSell.body',
    advance: Object.freeze({ type: 'click' }),
  }),
  Object.freeze({
    id: 'deposit', phase: 'economy', target: '#cityDepotButton', placement: 'top',
    titleKey: 'tut.deposit.title', bodyKey: 'tut.deposit.body',
    advance: Object.freeze({ type: 'click' }), ensure: 'city',
  }),
  Object.freeze({
    id: 'deposit-box', phase: 'economy', target: '.deposit-box-column', placement: 'left',
    titleKey: 'tut.depositBox.title', bodyKey: 'tut.depositBox.body',
    advance: Object.freeze({ type: 'next' }),
  }),

  // —— Fase 5 · PROGRESSÃO ——
  Object.freeze({
    id: 'profile', phase: 'progress', target: '#trainerProgressButton', placement: 'bottom',
    titleKey: 'tut.profile.title', bodyKey: 'tut.profile.body',
    advance: Object.freeze({ type: 'click' }), skipIf: mobileSkip,
  }),
  Object.freeze({
    id: 'nav-specialization', phase: 'progress', target: '#specializationNavButton', placement: 'bottom',
    titleKey: 'tut.navSpecialization.title', bodyKey: 'tut.navSpecialization.body',
    advance: Object.freeze({ type: 'click' }),
  }),
  Object.freeze({
    id: 'spec-invest', phase: 'progress', target: '[data-talent-upgrade]', placement: 'left',
    titleKey: 'tut.specInvest.title', bodyKey: 'tut.specInvest.body',
    advance: Object.freeze({ type: 'next' }),
  }),
  Object.freeze({
    id: 'systems-discovery', phase: 'progress', target: '#systemsDiscoveryGrid', placement: 'top',
    titleKey: 'tut.systemsDiscovery.title', bodyKey: 'tut.systemsDiscovery.body',
    advance: Object.freeze({ type: 'next' }),
  }),
  Object.freeze({
    id: 'nav-pokedex', phase: 'progress', target: '#pokedexNavButton', placement: 'bottom',
    titleKey: 'tut.navPokedex.title', bodyKey: 'tut.navPokedex.body',
    advance: Object.freeze({ type: 'click' }),
  }),

  // —— Fase 6 · AVANÇADO ——
  Object.freeze({
    id: 'nav-systems', phase: 'advanced', target: '#systemsNavButton', placement: 'bottom',
    titleKey: 'tut.navSystems.title', bodyKey: 'tut.navSystems.body',
    advance: Object.freeze({ type: 'click' }),
  }),
  Object.freeze({
    id: 'hub-upgrades', phase: 'advanced', target: '[data-open-panel="upgrades"]', placement: 'left',
    titleKey: 'tut.hubUpgrades.title', bodyKey: 'tut.hubUpgrades.body',
    advance: Object.freeze({ type: 'next' }),
  }),
  Object.freeze({
    id: 'nav-altar', phase: 'advanced', target: '#altarNavButton', placement: 'bottom',
    titleKey: 'tut.navAltar.title', bodyKey: 'tut.navAltar.body',
    advance: Object.freeze({ type: 'next' }), optional: true,
  }),
  Object.freeze({
    id: 'nav-breeding', phase: 'advanced', target: '#breedingNavButton', placement: 'bottom',
    titleKey: 'tut.navBreeding.title', bodyKey: 'tut.navBreeding.body',
    advance: Object.freeze({ type: 'next' }), optional: true,
  }),
  Object.freeze({
    id: 'daily-gift', phase: 'advanced', target: '#dailyGiftNavButton', placement: 'bottom',
    titleKey: 'tut.dailyGift.title', bodyKey: 'tut.dailyGift.body',
    advance: Object.freeze({ type: 'click' }),
  }),
  Object.freeze({
    id: 'game-pass', phase: 'advanced', target: '#gamePassNavButton', placement: 'bottom',
    titleKey: 'tut.gamePass.title', bodyKey: 'tut.gamePass.body',
    advance: Object.freeze({ type: 'next' }),
  }),

  // —— Fase 7 · SOCIAL ——
  Object.freeze({
    id: 'market', phase: 'social', target: '#marketNavButton', placement: 'bottom',
    titleKey: 'tut.market.title', bodyKey: 'tut.market.body',
    advance: Object.freeze({ type: 'click' }),
  }),
  Object.freeze({
    id: 'chat', phase: 'social', target: '#chatPanel', placement: 'right',
    titleKey: 'tut.chat.title', bodyKey: 'tut.chat.body',
    advance: Object.freeze({ type: 'next' }),
  }),
  Object.freeze({
    id: 'compare-lab', phase: 'social', target: '#pokemonCompareNavButton', placement: 'bottom',
    titleKey: 'tut.compareLab.title', bodyKey: 'tut.compareLab.body',
    advance: Object.freeze({ type: 'next' }), optional: true,
  }),
  Object.freeze({
    id: 'emerald-store', phase: 'social', target: '#emeraldStoreNavButton', placement: 'bottom',
    titleKey: 'tut.emeraldStore.title', bodyKey: 'tut.emeraldStore.body',
    advance: Object.freeze({ type: 'next' }),
  }),
]);

export function stepsForPhase(phaseId, steps = TUTORIAL_STEPS) {
  return steps.filter((step) => step.phase === phaseId);
}

export function stepById(id, steps = TUTORIAL_STEPS) {
  return steps.find((step) => step.id === id) || null;
}

export function phaseById(id, phases = TUTORIAL_PHASES) {
  return phases.find((phase) => phase.id === id) || null;
}

export function phaseTriggers(phase) {
  if (!phase) return [];
  if (Array.isArray(phase.triggers)) return [...phase.triggers];
  if (phase.trigger) return [phase.trigger];
  return [];
}

export function phaseByTrigger(eventName, phases = TUTORIAL_PHASES) {
  return phases.find((phase) => phaseTriggers(phase).includes(eventName)) || null;
}
